import type {
  LineFlexMessage,
  FlexContainer,
  DriverNotification,
  LineNotificationConfig,
  NotificationTemplate
} from '../types/line'
import type { ScheduleEntry } from '../types/schedule'
import type { Driver } from '../types/driver'
import { logServiceError } from '../utils/errorLogger'
import { apiCall } from '../utils/networkErrorHandler'
import { flexMessageBuilder } from './LineFlexMessageBuilder'
import { lineConfigLoader } from './LineConfigLoader'

export class LineNotificationServiceV2 {
  private config: LineNotificationConfig
  private templates: Map<string, NotificationTemplate> = new Map()
  private apiEndpoint: string

  constructor(config: LineNotificationConfig, apiEndpoint: string = '/api/line/send') {
    this.config = config
    this.apiEndpoint = apiEndpoint
    this.initializeTemplates()
  }

  /**
   * Initialize notification templates
   */
  private initializeTemplates(): void {
    // Assignment notification template
    this.templates.set('assignment', {
      type: 'assignment',
      altText: 'มีการมอบหมายงานขับรถใหม่',
      createMessage: (data: { schedule: ScheduleEntry; driver: Driver }) => 
        `🚗 งานขับรถใหม่

สวัสดีคุณ ${data.driver.fullName}
คุณได้รับมอบหมายงานขับรถใหม่

📅 วันที่: ${this.formatThaiDate(data.schedule.usageDate)}
⏰ เวลา: ${data.schedule.startTime} - ${data.schedule.endTime}
🚙 รถ: ${data.schedule.vehicle?.registrationNumber || 'ไม่ระบุ'}
🏷️ ประเภท: ${data.schedule.vehicle?.vehicleType || 'ไม่ระบุ'}${data.schedule.notes ? `\n\n📝 หมายเหตุ: ${data.schedule.notes}` : ''}`
    })

    // Reminder notification template
    this.templates.set('reminder', {
      type: 'reminder',
      altText: 'แจ้งเตือนงานขับรถ',
      createMessage: (data: { schedule: ScheduleEntry; driver: Driver; reminderTime: string }) => 
        `⏰ แจ้งเตือนงานขับรถ

สวัสดีคุณ ${data.driver.fullName}
คุณมีงานขับรถใน ${data.reminderTime}

📅 วันที่: ${this.formatThaiDate(data.schedule.usageDate)}
⏰ เวลา: ${data.schedule.startTime} - ${data.schedule.endTime}
🚙 รถ: ${data.schedule.vehicle?.registrationNumber || 'ไม่ระบุ'}`
    })

    // Cancellation notification template
    this.templates.set('cancellation', {
      type: 'cancellation',
      altText: 'ยกเลิกงานขับรถ',
      createMessage: (data: { schedule: ScheduleEntry; driver: Driver; reason?: string }) => 
        `❌ ยกเลิกงานขับรถ

สวัสดีคุณ ${data.driver.fullName}
งานขับรถของคุณถูกยกเลิก

📅 วันที่: ${this.formatThaiDate(data.schedule.usageDate)}
⏰ เวลา: ${data.schedule.startTime} - ${data.schedule.endTime}${data.reason ? `\n\n📝 เหตุผล: ${data.reason}` : ''}`
    })

    // Update notification template
    this.templates.set('update', {
      type: 'update',
      altText: 'อัปเดตงานขับรถ',
      createMessage: (data: { schedule: ScheduleEntry; driver: Driver; changes: string[] }) => 
        `📝 อัปเดตงานขับรถ

สวัสดีคุณ ${data.driver.fullName}
งานขับรถของคุณมีการเปลี่ยนแปลง

🔄 การเปลี่ยนแปลง:
${data.changes.map(change => `• ${change}`).join('\n')}

📅 วันที่: ${this.formatThaiDate(data.schedule.usageDate)}
⏰ เวลา: ${data.schedule.startTime} - ${data.schedule.endTime}`
    })
  }

  /**
   * Format date to Thai format
   */
  private formatThaiDate(date: Date | undefined): string {
    if (!date) {
      return 'ไม่ระบุวันที่'
    }

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ]

    const dateObj = date instanceof Date ? date : new Date(date)
    const day = dateObj.getDate()
    const month = thaiMonths[dateObj.getMonth()]
    const year = dateObj.getFullYear() + 543

    return `${day} ${month} ${year}`
  }

  /**
   * Send notification via API endpoint
   */
  private async sendViaAPI(userId: string, message: string, messageType: 'text' | 'flex' = 'text'): Promise<boolean> {
    try {
      // Use direct LINE API call via our handler (it will load config internally)
      const { sendLineMessage } = await import('../api/line-send-handler')

      const result = await sendLineMessage(userId, message, messageType)

      if (result.success) {
        console.log(`✅ LINE notification sent successfully`)
        return true
      } else {
        console.error(`❌ LINE API error: ${result.error}`, result.details)
        return false
      }
    } catch (error) {
      console.error('❌ Failed to send LINE notification via API:', error)
      return false
    }
  }

  /**
   * Send notification to driver with retry mechanism
   */
  async sendNotification(
    driverId: string,
    notificationType: 'assignment' | 'reminder' | 'cancellation' | 'update',
    data: any,
    maxRetries: number = 3
  ): Promise<boolean> {
    if (!this.config.enabled) {
      console.log('LINE notifications are disabled')
      return false
    }

    const template = this.templates.get(notificationType)
    if (!template) {
      throw new Error(`Unknown notification type: ${notificationType}`)
    }

    // Get driver's LINE user ID
    const lineUserId = await this.getDriverLineUserId(driverId)
    if (!lineUserId) {
      console.warn(`No LINE user ID found for driver: ${driverId}`)
      return false
    }

    // Create message
    const message = template.createMessage ? template.createMessage(data) : JSON.stringify(data)

    // Retry logic with exponential backoff
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const success = await this.sendViaAPI(lineUserId, message, 'flex')
        
        if (success) {
          console.log(`LINE notification sent successfully to driver: ${driverId} (attempt ${attempt})`)
          return true
        } else {
          throw new Error('API returned failure')
        }

      } catch (error) {
        const isLastAttempt = attempt === maxRetries
        const errorMessage = error instanceof Error ? error.message : String(error)
        
        console.warn(`LINE notification attempt ${attempt} failed for driver ${driverId}: ${errorMessage}`)
        
        if (isLastAttempt) {
          // Log final failure
          logServiceError(
            error instanceof Error ? error : new Error(String(error)),
            'LineNotificationServiceV2',
            'sendNotification',
            { driverId, notificationType, attempt, maxRetries }
          )
          
          // Store failed notification for manual retry
          this.storeFailedNotification(driverId, notificationType, data, errorMessage)
          return false
        }

        // Wait before retry with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000) // Max 10 seconds
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    return false
  }

  /**
   * Store failed notification for manual retry
   */
  private storeFailedNotification(
    driverId: string, 
    notificationType: string, 
    data: any, 
    error: string
  ): void {
    try {
      const failedNotifications = JSON.parse(
        localStorage.getItem('line_failed_notifications') || '[]'
      )
      
      failedNotifications.push({
        id: `failed_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        driverId,
        notificationType,
        data,
        error,
        timestamp: new Date().toISOString(),
        retryCount: 0
      })
      
      // Keep only last 50 failed notifications
      if (failedNotifications.length > 50) {
        failedNotifications.splice(0, failedNotifications.length - 50)
      }
      
      localStorage.setItem('line_failed_notifications', JSON.stringify(failedNotifications))
    } catch (error) {
      console.error('Failed to store failed notification:', error)
    }
  }

  /**
   * Send assignment notification
   */
  async sendAssignmentNotification(schedule: ScheduleEntry, driver: Driver): Promise<boolean> {
    // Load latest config from database
    const config = await lineConfigLoader.loadConfig()

    if (!config.enabled) {
      console.log('LINE notifications are disabled')
      return false
    }

    const lineUserId = await this.getDriverLineUserId(driver.id)
    if (!lineUserId) {
      console.warn(`No LINE user ID found for driver: ${driver.id}`)
      return false
    }

    // Debug: Check vehicle image
    console.log('🚗 Vehicle info:', {
      registrationNumber: schedule.vehicle?.registrationNumber,
      imageUrl: schedule.vehicle?.imageUrl,
      hasImage: !!schedule.vehicle?.imageUrl
    })

    // Use flex message builder
    const flexMessage = flexMessageBuilder.buildAssignmentMessage(schedule, driver)

    return this.sendViaAPI(lineUserId, flexMessage, 'flex')
  }

  /**
   * Send reminder notification
   */
  async sendReminderNotification(
    schedule: ScheduleEntry,
    driver: Driver,
    reminderTime: string
  ): Promise<boolean> {
    const config = await lineConfigLoader.loadConfig()

    if (!config.enabled) {
      console.log('LINE notifications are disabled')
      return false
    }

    const lineUserId = await this.getDriverLineUserId(driver.id)
    if (!lineUserId) {
      console.warn(`No LINE user ID found for driver: ${driver.id}`)
      return false
    }

    const flexMessage = flexMessageBuilder.buildReminderMessage(schedule, driver, reminderTime)

    return this.sendViaAPI(lineUserId, flexMessage, 'flex')
  }

  /**
   * Send cancellation notification
   */
  async sendCancellationNotification(
    schedule: ScheduleEntry,
    driver: Driver,
    reason?: string
  ): Promise<boolean> {
    const config = await lineConfigLoader.loadConfig()

    if (!config.enabled) {
      console.log('LINE notifications are disabled')
      return false
    }

    const lineUserId = await this.getDriverLineUserId(driver.id)
    if (!lineUserId) {
      console.warn(`No LINE user ID found for driver: ${driver.id}`)
      return false
    }

    const flexMessage = flexMessageBuilder.buildCancellationMessage(schedule, driver, reason)

    return this.sendViaAPI(lineUserId, flexMessage, 'flex')
  }

  /**
   * Send update notification
   */
  async sendUpdateNotification(
    schedule: ScheduleEntry,
    driver: Driver,
    changes: string[]
  ): Promise<boolean> {
    const config = await lineConfigLoader.loadConfig()

    if (!config.enabled) {
      console.log('LINE notifications are disabled')
      return false
    }

    const lineUserId = await this.getDriverLineUserId(driver.id)
    if (!lineUserId) {
      console.warn(`No LINE user ID found for driver: ${driver.id}`)
      return false
    }

    // Debug: Check vehicle image
    console.log('🚗 Vehicle info (Update):', {
      registrationNumber: schedule.vehicle?.registrationNumber,
      imageUrl: schedule.vehicle?.imageUrl,
      hasImage: !!schedule.vehicle?.imageUrl
    })

    // Use flex message builder
    const flexMessage = flexMessageBuilder.buildUpdateMessage(schedule, driver, changes)

    return this.sendViaAPI(lineUserId, flexMessage, 'flex')
  }

  /**
   * Get driver's LINE user ID from database
   */
  private async getDriverLineUserId(driverId: string): Promise<string | null> {
    try {
      const { supabase } = await import('../lib/supabase')
      
      const { data, error } = await supabase
        .from('drivers')
        .select('line_user_id')
        .eq('id', driverId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // Driver not found
        }
        throw new Error(`Failed to get driver LINE user ID: ${error.message}`)
      }

      return data.line_user_id || null
    } catch (error) {
      logServiceError(
        error instanceof Error ? error : new Error(String(error)),
        'LineNotificationServiceV2',
        'getDriverLineUserId',
        { driverId }
      )
      return null
    }
  }

  /**
   * Test notification sending
   */
  async testNotification(driverId: string): Promise<boolean> {
    const testMessage = `🧪 ทดสอบระบบแจ้งเตือน LINE

สวัสดีครับ! นี่คือการทดสอบระบบแจ้งเตือน

⏰ ${new Date().toLocaleString('th-TH')}
🔧 ส่งจาก LineNotificationServiceV2`

    // Get driver's LINE user ID
    const lineUserId = await this.getDriverLineUserId(driverId)
    if (!lineUserId) {
      console.warn(`No LINE user ID found for driver: ${driverId}`)
      return false
    }

    return this.sendViaAPI(lineUserId, testMessage, 'text')
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<LineNotificationConfig>): void {
    this.config = { ...this.config, ...config }
  }
}

// Export singleton instance with lazy initialization
// Config will be loaded from database when first needed
export const lineNotificationServiceV2 = new LineNotificationServiceV2({
  channelAccessToken: '', // Will be loaded from database via LineConfigLoader
  enabled: false // Will be loaded from database via LineConfigLoader
})